<template>
  <div>
    <h5 class="mt-4">{{ $t('gc-rfConfigFormMono:modem') | capitalize }}</h5>

    <div class="table-responsive">
      <table class="table table-sm table-borderless mb-0">
        <thead>
          <tr class="text-center">
            <th>{{ $t('gc-rfConfigFormMono:frequencyInterval') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:radiopathWidth') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:radiopathIntervals') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:antennaGain') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:insertionLoss') | capitalize }}</th>
          </tr>
        </thead>
        <tbody>
          <tr class="text-center">
            <td>{{ $t('gc-rfConfigFormMono:frequencyIntervalValues', rfConstants.frequencyInterval[frequency]) }}</td>
            <td>{{ rfConstants.radioBandwidth }} MHz</td>
            <td>
              <div class="mb-1">
                <span>{{ round4(Number(radio0Frequency) - rfConstants.radioBandwidth / 2) }} MHz - </span>
                <input id="radio_0"
                       v-model="radio0Frequency"
                       v-validate="'required|number'"
                       :class="{ 'is-invalid': errors.has('radio_0') }"
                       :data-vv-as="$t('gc-rfConfigFormMono:frequencyInterval')"
                       type="text"
                       name="radio_0"
                       class="mb-1 form-control form-control-sm"
                > MHz
                <span>- {{ round4(Number(radio0Frequency) + rfConstants.radioBandwidth / 2) }} MHz</span>
                <div class="invalid-feedback">{{ errors.first('radio_0') }}</div>
              </div>

              <div>
                <span>{{ round4(Number(radio1Frequency) - rfConstants.radioBandwidth / 2) }} MHz - </span>
                <input id="radio_1"
                       v-model="radio1Frequency"
                       v-validate="'required|number'"
                       :class="{ 'is-invalid': errors.has('radio_1') }"
                       :data-vv-as="$t('gc-rfConfigFormMono:frequencyInterval')"
                       type="text"
                       name="radio_1"
                       class="mb-1 form-control form-control-sm"
                > MHz
                <span>- {{ round4(Number(radio1Frequency) + rfConstants.radioBandwidth / 2) }} MHz</span>
                <div class="invalid-feedback">{{ errors.first('radio_1') }}</div>
              </div>
            </td>
            <td>
              <input id="antennaGain"
                     v-model="antennaGain"
                     v-validate="'number'"
                     :class="{ 'is-invalid': errors.has('antennaGain') }"
                     :data-vv-as="$t('gc-rfConfigFormMono:antennaGain')"
                     type="text"
                     name="antennaGain"
                     class="mb-1 form-control form-control-sm"
              > dBi
              <div class="invalid-feedback">{{ errors.first('antennaGain') }}</div>
            </td>
            <td>
              <input id="insertionLoss"
                     v-model="insertionLoss"
                     v-validate="'number'"
                     :class="{ 'is-invalid': errors.has('insertionLoss') }"
                     :data-vv-as="$t('gc-rfConfigFormMono:insertionLoss')"
                     type="text"
                     name="insertionLoss"
                     class="mb-1 form-control form-control-sm"
              > dB
              <div class="invalid-feedback">{{ errors.first('insertionLoss') }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <h5 class="mt-3">{{ $t('gc-rfConfigFormMono:channels') | capitalize }}</h5>

    <div class="table-responsive">
      <table class="table table-sm table-borderless mb-0">
        <thead>
          <tr class="text-center">
            <th>{{ $t('gc-rfConfigFormMono:name') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:effective') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:radio') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:spreadingFactor') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:channelFrequency') | capitalize }}</th>
            <th>{{ $t('gc-rfConfigFormMono:bandwidth') | capitalize }}</th>
          </tr>
        </thead>

        <tbody>
          <!-- Multi SF -->
          <tr v-for="(channel, index) in multiSf" :key="index" class="text-center">
            <td>{{ channel.name }}</td>
            <td>
              <input :id="'enableChannel' + index" v-model="channel.enable" :name="'enableChannel' + index" type="checkbox">
              <label :for="'enableChannel' + index" />
            </td>
            <td>
              <select :id="'radioMultiSF' + index" v-model="channel.radio" class="form-control form-control-sm">
                <option :value="0">0</option>
                <option :value="1">1</option>
              </select>
            </td>
            <td>{{ channel.spread_factor }}</td>
            <td>
              <input :id="'channelFrequencyMultiSF' + index"
                     v-model="channel.freq"
                     v-validate="{ 'required': channel.enable, 'number': true }"
                     :class="{ 'is-invalid': errors.has('channelFrequencyMultiSF' + index) }"
                     :name="'channelFrequencyMultiSF' + index"
                     :data-vv-as="$t('gc-rfConfigFormMono:channelFrequency')"
                     type="text"
                     class="form-control form-control-sm"
              > MHz
              <div class="invalid-feedback">{{ errors.first('channelFrequencyMultiSF' + index) }}</div>
            </td>
            <td>{{ channel.bandwidth * 1000 }} kHz</td>
          </tr>

          <!-- Mono SF -->
          <tr v-if="hasMonoSf" class="text-center">
            <td>{{ monoSf.name }}</td>
            <td>
              <input id="enableMonoChannel" v-model="monoSf.enable" name="enableMonoChannel" type="checkbox">
              <label for="enableMonoChannel" />
            </td>
            <td>
              <select id="monoSfRadio" v-model="monoSf.radio" class="form-control form-control-sm">
                <option :value="0">0</option>
                <option :value="1">1</option>
              </select>
            </td>
            <td>
              <select id="monoSfSpreadFactor" v-model="monoSf.spread_factor" class="form-control form-control-sm">
                <option :value="7">7</option>
                <option :value="8">8</option>
                <option :value="9">9</option>
                <option :value="10">10</option>
                <option :value="11">11</option>
                <option :value="12">12</option>
              </select>
            </td>
            <td>
              <input id="channelFrequencyMonoSf"
                     v-model="monoSf.freq"
                     v-validate="{ 'required': monoSf.enable, 'number': true }"
                     :class="{ 'is-invalid': errors.has('channelFrequencyMonoSf') }"
                     :data-vv-as="$t('gc-rfConfigFormMono:channelFrequency')"
                     type="text"
                     name="channelFrequencyMonoSf"
                     class="mb-1 form-control form-control-sm"
              > MHz
              <div class="invalid-feedback">{{ errors.first('channelFrequencyMonoSf') }}</div>
            </td>
            <td>
              <select id="monoSfBandwidth" v-model="monoSf.bandwidth" class="form-control form-control-sm">
                <option :value="125000">125</option>
                <option :value="250000">250</option>
                <option :value="500000">500</option>
              </select> kHz
            </td>
          </tr>

          <!-- FSK -->
          <tr v-if="hasFsk" class="text-center">
            <td>{{ fsk.name }}</td>
            <td>
              <input id="enableFskChannel" v-model="fsk.enable" name="enableFskChannel" type="checkbox">
              <label for="enableFskChannel" />
            </td>
            <td>
              <select id="fskRadio" v-model="fsk.radio" class="form-control form-control-sm">
                <option :value="0">0</option>
                <option :value="1">1</option>
              </select>
            </td>
            <td>{{ fsk.spread_factor }}</td>
            <td>
              <input id="channelFrequencyFSK"
                     v-model="fsk.freq"
                     v-validate="{ 'required': fsk.enable, 'number': true }"
                     :class="{ 'is-invalid': errors.has('channelFrequencyFSK') }"
                     :data-vv-as="$t('gc-rfConfigFormMono:channelFrequency')"
                     type="text"
                     name="channelFrequencyFSK"
                     class="mb-1 form-control form-control-sm"
              > MHz
              <div class="invalid-feedback">{{ errors.first('channelFrequencyFSK') }}</div>
            </td>
            <td>{{ fsk.bandwidth / 1000 }} kHz</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h5 v-if="gatewayConf.beacon_enable !== undefined" class="mt-4">{{ $t('gc-rfConfigFormMono:gateway') | capitalize }}</h5>

    <div v-if="gatewayConf.beacon_enable !== undefined" class="mt-2 px-3">
      <!-- GATEWAY CONFIGURATION -->
      <div class="form-group row mb-0">
        <label class="col-sm-3 col-form-label">{{ $t('gc-rfConfigFormMono:supportClassB') | capitalize }}</label>
        <div class="py-2">
          <input id="gwBeaconEnable" v-model="gatewayConf.beacon_enable" type="checkbox">
          <label for="gwBeaconEnable" />
          <label for="gwBeaconEnable" class="mr-5">{{ $t('gc-rfConfigFormMono:beaconEnable') | capitalize }}</label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep, isEmpty, round, each, isNil } from 'lodash-es'
import { mono as rfConstants } from '@/configuration/rfConfig'

export default {
  model: {
    prop: 'rfConfig',
    event: 'change'
  },

  props: {
    rfConfig: {
      type: String,
      required: true
    },
    radioType: {
      type: String,
      required: true
    },
    frequency: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      radio0Frequency: '',
      radio1Frequency: '',
      antennaGain: '',
      insertionLoss: '',
      multiSf: {},
      monoSf: {},
      fsk: {},
      // Gateway configuration
      gatewayConf: {}
    }
  },

  computed: {
    rfConstants () { return rfConstants },

    rfConfigObject () {
      // Return default object before getting data
      if (!this.rfConfig && this.radioType === 'SX1301') return { SX1301_conf: {}, gateway_conf: {} }
      if (!this.rfConfig && this.radioType === 'SX130x') return { SX130x_conf: {}, gateway_conf: {} }
      // Remove comments and parse string into object
      return JSON.parse(cloneDeep(this.rfConfig).replace(/\/\*[^*]*\*\//g, ''))
    },

    hasMonoSf () { return !isEmpty(this.monoSf) },
    hasFsk () { return !isEmpty(this.fsk) },

    // Computed property used to watch changes
    formDataProvider () {
      return {
        radio0Frequency: this.radio0Frequency,
        radio1Frequency: this.radio1Frequency,
        antennaGain: this.antennaGain,
        insertionLoss: this.insertionLoss,
        multiSf: this.multiSf,
        monoSf: this.monoSf,
        fsk: this.fsk,
        gatewayConf: this.gatewayConf
      }
    }
  },

  watch: {
    rfConfigObject (newConfig, oldConfig) {
      let oConf = null
      if (this.radioType === 'SX1301' && oldConfig.SX1301_conf) {
        oConf = oldConfig.SX1301_conf
      }
      if (this.radioType === 'SX130x' && oldConfig.SX130x_conf) {
        oConf = oldConfig.SX130x_conf
      }
      const isOldValuesEmpty = !oldConfig || (isEmpty(oConf) && isEmpty(oldConfig.gateway_conf))

      // Create formDataProvider only the first time to prevent "change" to be triggered twice
      if (isOldValuesEmpty) {
        this.createFormDataProvider()
      }
    },

    formDataProvider: {
      handler: function (newValues, oldValues) {
        // Create rfConfig string and update v-model
        this.$emit('change', this.recreateRfConfig())

        // Validate form
        this.validateForm()
      },
      deep: true
    }
  },

  created () {
    this.createFormDataProvider()
  },

  methods: {
    // Wrapper to round to be used in template
    round4 (x) {
      return round(x, 4)
    },

    createFormDataProvider () {
      // Return default object before getting data
      if (this.radioType === 'SX1301' && isEmpty(this.rfConfigObject.SX1301_conf)) return
      if (this.radioType === 'SX130x' && isEmpty(this.rfConfigObject.SX130x_conf)) return

      // Gateway configuration
      this.gatewayConf = cloneDeep(this.rfConfigObject.gateway_conf) || {}

      // Radio configuration
      let rfConfig = {}
      if (this.radioType === 'SX1301') {
        rfConfig = this.rfConfigObject.SX1301_conf
      }
      if (this.radioType === 'SX130x') {
        rfConfig = this.rfConfigObject.SX130x_conf
      }

      // Radios
      const radios = [rfConfig.radio_0, rfConfig.radio_1]
      this.radio0Frequency = radios[0].freq / 1000000
      this.radio1Frequency = radios[1].freq / 1000000

      // Antenna
      this.antennaGain = rfConfig.antenna_gain
      this.insertionLoss = rfConfig.insertion_loss

      // Multi SF
      const multiSf = [rfConfig.chan_multiSF_0, rfConfig.chan_multiSF_1, rfConfig.chan_multiSF_2, rfConfig.chan_multiSF_3, rfConfig.chan_multiSF_4, rfConfig.chan_multiSF_5, rfConfig.chan_multiSF_6, rfConfig.chan_multiSF_7]
      each(multiSf, (channel, index) => {
        channel.name = `chan_multiSF_${index}`
        channel.spread_factor = 'Multi SF'
        channel.freq = channel.if === null ? null : (radios[channel.radio].freq + channel.if) / 1000000
        channel.bandwidth = rfConstants.multiSfBandwidth
      })
      this.multiSf = multiSf

      // Mono SF
      if (rfConfig.chan_Lora_std) {
        this.monoSf = {
          name: 'chan_Lora_std',
          enable: rfConfig.chan_Lora_std.enable,
          radio: rfConfig.chan_Lora_std.radio,
          spread_factor: rfConfig.chan_Lora_std.spread_factor,
          freq: rfConfig.chan_Lora_std.if === null ? null : (radios[rfConfig.chan_Lora_std.radio].freq + rfConfig.chan_Lora_std.if) / 1000000,
          bandwidth: rfConfig.chan_Lora_std.bandwidth
        }
      }

      // FSK
      if (rfConfig.chan_FSK) {
        this.fsk = {
          name: 'chan_FSK',
          enable: rfConfig.chan_FSK.enable,
          radio: rfConfig.chan_FSK.radio,
          spread_factor: '',
          freq: rfConfig.chan_FSK.if === null ? null : (radios[rfConfig.chan_FSK.radio].freq + rfConfig.chan_FSK.if) / 1000000,
          bandwidth: rfConfig.chan_FSK.bandwidth
        }
      }
    },

    recreateRfConfig () {
      var rfConfigObject = {}
      if (this.radioType === 'SX1301') {
        rfConfigObject = cloneDeep(this.rfConfigObject.SX1301_conf)
      }
      if (this.radioType === 'SX130x') {
        rfConfigObject = cloneDeep(this.rfConfigObject.SX130x_conf)
      }

      const radios = [this.radio0Frequency, this.radio1Frequency]

      // Radios
      rfConfigObject.radio_0.freq = this.radio0Frequency * 1000000
      rfConfigObject.radio_1.freq = this.radio1Frequency * 1000000

      // Antenna
      if (isNil(rfConfigObject.antenna_gain) || rfConfigObject.antenna_gain === '') delete rfConfigObject.antenna_gain
      else rfConfigObject.antenna_gain = Number(this.antennaGain)
      if (isNil(rfConfigObject.insertion_loss) || rfConfigObject.insertion_loss === '') delete rfConfigObject.insertion_loss
      else rfConfigObject.insertion_loss = Number(this.insertionLoss)

      // Multi SF
      each(this.multiSf, (channel, index) => {
        rfConfigObject[channel.name].enable = channel.enable
        rfConfigObject[channel.name].radio = channel.radio
        if (channel.freq === null || channel.freq === '') {
          rfConfigObject[channel.name].if = null
        } else {
          rfConfigObject[channel.name].if = Math.round((channel.freq - radios[channel.radio]) * 1000000)
        }
        // Remove added properties
        delete rfConfigObject[channel.name].name
        delete rfConfigObject[channel.name].spread_factor
        delete rfConfigObject[channel.name].freq
        delete rfConfigObject[channel.name].bandwidth
      })

      // Mono SF
      if (this.hasMonoSf) {
        rfConfigObject.chan_Lora_std.enable = this.monoSf.enable
        rfConfigObject.chan_Lora_std.radio = this.monoSf.radio
        if (this.monoSf.freq === null || this.monoSf.freq === '') {
          rfConfigObject.chan_Lora_std.if = null
        } else {
          rfConfigObject.chan_Lora_std.if = Math.round((this.monoSf.freq - radios[this.monoSf.radio]) * 1000000)
        }
        rfConfigObject.chan_Lora_std.spread_factor = this.monoSf.spread_factor
        rfConfigObject.chan_Lora_std.bandwidth = this.monoSf.bandwidth
      }

      // FSK
      if (this.hasFsk) {
        rfConfigObject.chan_FSK.enable = this.fsk.enable
        rfConfigObject.chan_FSK.radio = this.fsk.radio
        if (this.fsk.freq === null || this.fsk.freq === '') {
          rfConfigObject.chan_FSK.if = null
        } else {
          rfConfigObject.chan_FSK.if = Math.round((this.fsk.freq - radios[this.fsk.radio]) * 1000000)
        }
      }

      if (this.radioType === 'SX1301') {
        return JSON.stringify({ SX1301_conf: rfConfigObject, gateway_conf: this.gatewayConf })
      }
      if (this.radioType === 'SX130x') {
        return JSON.stringify({ SX130x_conf: rfConfigObject, gateway_conf: this.gatewayConf })
      }
    },

    async validateForm () {
      // BUGFIX: Need double validations in order to correctly manage channel frequency required constraint depending on channel effectiveness
      await this.$validator.validateAll()
      const valid = await this.$validator.validateAll()

      this.$emit('validate', valid)

      return valid
    }
  }
}
</script>

<style scoped lang="scss">
  .form-control {
    display: inline-block;
  }

  input.form-control {
    width: 55px;
  }

  select.form-control {
    width: 60px;
  }
</style>
